import React from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import { useDashboardProfile } from 'pages/useDashboardProfile';
import {
  LearnAssignmentResult,
  listLearnAssignment,
  listLearnQuestions,
} from 'stores/TrainingQuestionStoreAdapter';
import { errorDefault } from 'utils/Toastify';

const ACCENT = '#00838f';

const pct = (cleared: number, total: number) =>
  total === 0 ? 0 : Math.round((cleared / total) * 100);

/** 未クリア設問（重複排除済み・遷移用 assignmentId 付き）。 */
type UnclearedQuestion = {
  questionId: string;
  title: string;
  assignmentId: string;
  cleared: boolean;
};

// --- ウィジェット外枠 ---
const Widget: React.FC<{
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, action, children }) => (
  <Card
    elevation={0}
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 3,
      p: 2.5,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      {action}
    </Stack>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Card>
);

// --- コンパクトな KPI タイル ---
const StatTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  suffix: string;
  color: string;
  loading: boolean;
  onClick?: () => void;
}> = ({ icon, label, value, suffix, color, loading, onClick }) => {
  const clickable = !!onClick;
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        p: 1.75,
        height: '100%',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        ...(clickable && {
          '&:hover': {
            borderColor: color,
            transform: 'translateY(-2px)',
            '& .stat-arrow': { opacity: 1, transform: 'translateX(0)' },
          },
        }),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <Box
          sx={(t) => ({
            width: 30,
            height: 30,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(color, 0.12),
            color,
            flexShrink: 0,
            ...t.applyStyles('dark', { bgcolor: alpha(color, 0.24) }),
          })}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          noWrap
          sx={{ flex: 1 }}
        >
          {label}
        </Typography>
        {clickable && (
          <ArrowForwardIcon
            className="stat-arrow"
            sx={{
              fontSize: 15,
              color,
              opacity: 0,
              transform: 'translateX(-4px)',
              transition: 'opacity 0.2s, transform 0.2s',
              flexShrink: 0,
            }}
          />
        )}
      </Stack>
      {loading ? (
        <Skeleton variant="text" width={56} height={32} />
      ) : (
        <Stack direction="row" alignItems="baseline" spacing={0.5}>
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {suffix}
          </Typography>
        </Stack>
      )}
    </Card>
  );
};

/**
 * 研修受講ドメインのトップ（全ユーザー）。
 * 学習者が「次に何をすべきか」を最短で掴めることを優先したダッシュボード:
 * - ヒーロー
 * - 続きから（最優先アクション：進行中で最も進捗の低い課題）
 * - KPI ストリップ（割当 / 完了 / 取組中・未着手 / クリア設問）
 * - 課題別の進捗 ＋ 未クリアの設問（2カラム）
 */
const TrainingLearnHome: React.FC = () => {
  const navigate = useNavigate();

  const [assignments, setAssignments] = React.useState<LearnAssignmentResult[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [uncleared, setUncleared] = React.useState<UnclearedQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  // 課題一覧を取得する。初回ロードと更新ボタンで共用。
  const reloadAssignments = React.useCallback(async () => {
    try {
      const list = await listLearnAssignment();
      setAssignments(list);
    } catch {
      errorDefault('課題の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  // 未クリア設問の収集（AllQuestions と同じ合成方式・questionId で重複排除）。初回ロードと更新ボタンで共用。
  const reloadUncleared = React.useCallback(async () => {
    setQuestionsLoading(true);
    try {
      const list = await listLearnAssignment();
      const perAssignment = await Promise.all(
        list.map((a) =>
          listLearnQuestions(a.assignmentId)
            .then((qs) => ({ assignmentId: a.assignmentId, questions: qs }))
            .catch(() => ({ assignmentId: a.assignmentId, questions: [] })),
        ),
      );
      const map = new Map<string, UnclearedQuestion>();
      perAssignment.forEach(({ assignmentId, questions }) => {
        questions.forEach((q) => {
          const existing = map.get(q.questionId);
          if (!existing) {
            map.set(q.questionId, {
              questionId: q.questionId,
              title: q.title,
              assignmentId,
              cleared: q.cleared,
            });
          } else if (q.cleared) {
            existing.cleared = true;
          }
        });
      });
      setUncleared(Array.from(map.values()).filter((q) => !q.cleared));
    } catch {
      /* ベストエフォート */
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    reloadAssignments();
  }, [reloadAssignments]);

  React.useEffect(() => {
    reloadUncleared();
  }, [reloadUncleared]);

  // ヒーローの更新ボタン: 課題一覧と未クリア設問をまとめて再取得する
  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([reloadAssignments(), reloadUncleared()]);
    } finally {
      setRefreshing(false);
    }
  }, [reloadAssignments, reloadUncleared]);

  // --- 集計 ---
  const stats = React.useMemo(() => {
    const totalQuestions = assignments.reduce((s, a) => s + a.totalQuestionCount, 0);
    const clearedQuestions = assignments.reduce((s, a) => s + a.clearedQuestionCount, 0);
    const completed = assignments.filter(
      (a) => a.totalQuestionCount > 0 && a.clearedQuestionCount >= a.totalQuestionCount,
    ).length;
    const inProgress = assignments.filter(
      (a) => a.clearedQuestionCount > 0 && a.clearedQuestionCount < a.totalQuestionCount,
    ).length;
    const notStarted = assignments.filter((a) => a.clearedQuestionCount === 0).length;
    return {
      assignmentCount: assignments.length,
      totalQuestions,
      clearedQuestions,
      overallPct: pct(clearedQuestions, totalQuestions),
      completed,
      inProgress,
      notStarted,
      allDone: assignments.length > 0 && completed === assignments.length,
    };
  }, [assignments]);

  // 課題別の進捗（進捗率の低い順＝取り組むべき順）
  const ranked = React.useMemo(
    () =>
      [...assignments]
        .map((a) => ({ ...a, rate: pct(a.clearedQuestionCount, a.totalQuestionCount) }))
        .sort((a, b) => a.rate - b.rate),
    [assignments],
  );

  // 「続きから」対象: 割り当てられている未完了の課題（進捗の高い順＝あと少しで終わる順）。
  // 取組中（cleared>0）を優先し、その後に未着手を並べる。
  const resumeList = React.useMemo(() => {
    const incomplete = ranked.filter((a) => a.rate < 100 || a.totalQuestionCount === 0);
    const inProgress = incomplete
      .filter((a) => a.clearedQuestionCount > 0)
      .sort((a, b) => b.rate - a.rate);
    const notStarted = incomplete.filter((a) => a.clearedQuestionCount === 0);
    return [...inProgress, ...notStarted];
  }, [ranked]);

  // 表示中の課題インデックス ＋ スライド方向（'next' / 'prev'）
  const [resumeIndex, setResumeIndex] = React.useState(0);
  const [slideDir, setSlideDir] = React.useState<'next' | 'prev'>('next');
  // 一巡アニメーションのためのキー（切り替えのたびに変える）
  const [slideKey, setSlideKey] = React.useState(0);

  // リスト件数が変わったらインデックスを丸める
  React.useEffect(() => {
    setResumeIndex((i) => (resumeList.length === 0 ? 0 : Math.min(i, resumeList.length - 1)));
  }, [resumeList.length]);

  const resumeTarget = resumeList[resumeIndex] ?? null;
  const goResume = (dir: 'next' | 'prev') => {
    if (resumeList.length <= 1) return;
    setSlideDir(dir);
    setSlideKey((k) => k + 1);
    setResumeIndex((i) =>
      dir === 'next'
        ? (i + 1) % resumeList.length
        : (i - 1 + resumeList.length) % resumeList.length,
    );
  };

  // 課題別の進捗: クリア済み / 未クリア の切り替え（既定: 未クリア）。user_profile(AUTO) で保存/復元。
  const [learnDashboard, setLearnDashboard] = useDashboardProfile('training-learn-home-dashboard', {
    progressFilter: 'uncleared' as 'uncleared' | 'cleared',
  });
  const progressFilter = learnDashboard.progressFilter;
  const setProgressFilter = React.useCallback(
    (v: 'uncleared' | 'cleared') => setLearnDashboard((prev) => ({ ...prev, progressFilter: v })),
    [setLearnDashboard],
  );
  const rankedFiltered = React.useMemo(() => {
    const isCleared = (a: (typeof ranked)[number]) => a.rate === 100 && a.totalQuestionCount > 0;
    return progressFilter === 'cleared'
      ? ranked.filter(isCleared)
      : ranked.filter((a) => !isCleared(a));
  }, [ranked, progressFilter]);

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 1100 }}>
        {/* ヒーロー */}
        <DomainHero
          eyebrow="Programming Training"
          title="Sharpen Your Algorithms"
          tagline="Work through the coding challenges assigned to you and build the habit of thinking algorithms through on your own — one language, one problem at a time."
          palette={HERO_PALETTES.training}
          onBack={() => navigate('/')}
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />

        {/* 続きから（最優先アクション） */}
        {!loading &&
          (assignments.length === 0 ? (
            <Card
              elevation={0}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 3, p: 3, textAlign: 'center' }}
            >
              <Typography variant="body2" color="text.secondary">
                割り当てられている課題はありません
              </Typography>
            </Card>
          ) : (
            <Card
              elevation={0}
              sx={(t) => ({
                borderRadius: 3,
                p: 2.5,
                border: 1,
                borderColor: 'divider',
                bgcolor: alpha(ACCENT, 0.06),
                overflow: 'hidden',
                ...t.applyStyles('dark', { bgcolor: alpha(ACCENT, 0.14) }),
              })}
            >
              {stats.allDone || !resumeTarget ? (
                // すべて完了 or 未完了なし
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 'grow' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      すべて達成
                    </Typography>
                    <Typography variant="h6" fontWeight={700} noWrap sx={{ mt: 0.25 }}>
                      おつかれさまでした 🎉
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 'auto' }}>
                    <Button
                      variant="contained"
                      startIcon={<MenuBookIcon />}
                      onClick={() => navigate('/training-learn/learn-assignment')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2,
                        boxShadow: 'none',
                        bgcolor: ACCENT,
                        '&:hover': { boxShadow: 'none', bgcolor: '#00707a' },
                      }}
                    >
                      課題一覧を見る
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Stack spacing={1}>
                  {/* ヘッダー: ラベル ＋ 前後ナビ */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      続きから（{resumeIndex + 1} / {resumeList.length}）
                    </Typography>
                    {resumeList.length > 1 && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => goResume('prev')}
                          aria-label="前の課題"
                        >
                          <ChevronLeftIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                        <Typography variant="caption" color="text.secondary">
                          前の課題 / 次の課題
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => goResume('next')}
                          aria-label="次の課題"
                        >
                          <ChevronRightIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Stack>
                    )}
                  </Stack>

                  {/* スワイプするコンテンツ（切り替え時に左右へスライド） */}
                  <Box
                    key={slideKey}
                    sx={{
                      // 画面外から大きく左右へスライドインする（スワイプ感を強調）
                      '@keyframes resumeFromRight': {
                        '0%': { transform: 'translateX(100%)', opacity: 0 },
                        '60%': { opacity: 1 },
                        '100%': { transform: 'translateX(0)', opacity: 1 },
                      },
                      '@keyframes resumeFromLeft': {
                        '0%': { transform: 'translateX(-100%)', opacity: 0 },
                        '60%': { opacity: 1 },
                        '100%': { transform: 'translateX(0)', opacity: 1 },
                      },
                      animation: `${slideDir === 'next' ? 'resumeFromRight' : 'resumeFromLeft'} 0.38s cubic-bezier(0.22, 0.61, 0.36, 1)`,
                      willChange: 'transform',
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, sm: 'grow' }}>
                        <Typography variant="h6" fontWeight={700} noWrap>
                          {resumeTarget.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {resumeTarget.clearedQuestionCount} / {resumeTarget.totalQuestionCount}{' '}
                          問クリア（
                          {pct(resumeTarget.clearedQuestionCount, resumeTarget.totalQuestionCount)}
                          %）
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={pct(
                            resumeTarget.clearedQuestionCount,
                            resumeTarget.totalQuestionCount,
                          )}
                          sx={{ mt: 0.5, height: 6, borderRadius: 3, maxWidth: 360 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 'auto' }}>
                        <Button
                          variant="contained"
                          startIcon={<PlayArrowIcon />}
                          onClick={() =>
                            navigate(
                              `/training-learn/learn-assignment/${resumeTarget.assignmentId}`,
                            )
                          }
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 2,
                            boxShadow: 'none',
                            bgcolor: ACCENT,
                            '&:hover': { boxShadow: 'none', bgcolor: '#00707a' },
                          }}
                        >
                          この課題を続ける
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              )}
            </Card>
          ))}

        {/* KPI ストリップ */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<MenuBookIcon sx={{ fontSize: 16 }} />}
              label="課題"
              value={stats.assignmentCount}
              suffix="件"
              color={ACCENT}
              loading={loading}
              onClick={() => navigate('/training-learn/learn-assignment')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
              label="完了"
              value={stats.completed}
              suffix="件"
              color="#2e7d32"
              loading={loading}
              onClick={() => navigate('/training-learn/learn-assignment?filter=completed')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<HourglassEmptyIcon sx={{ fontSize: 16 }} />}
              label="未完了"
              value={stats.inProgress + stats.notStarted}
              suffix="件"
              color="#ed6c02"
              loading={loading}
              onClick={() => navigate('/training-learn/learn-assignment?filter=incomplete')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<PlaylistAddCheckIcon sx={{ fontSize: 16 }} />}
              label="クリア設問"
              value={stats.clearedQuestions}
              suffix="問"
              color="#1565c0"
              loading={loading}
              onClick={() => navigate('/training-learn/all-questions?filter=cleared')}
            />
          </Grid>
        </Grid>

        {/* 2カラム: 課題別の進捗 ＋ 未クリアの設問 */}
        <Grid container spacing={2}>
          {/* 課題別の進捗 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              title="課題別の進捗"
              action={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={progressFilter}
                    onChange={(_e, v) => {
                      if (v !== null) setProgressFilter(v);
                    }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: 1,
                        py: 0.25,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                      },
                    }}
                  >
                    <ToggleButton value="uncleared">未クリア</ToggleButton>
                    <ToggleButton value="cleared">クリア済み</ToggleButton>
                  </ToggleButtonGroup>
                  <Link
                    component="button"
                    variant="caption"
                    underline="hover"
                    onClick={() => navigate('/training-learn/learn-assignment')}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                  >
                    すべての課題
                    <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </Link>
                </Stack>
              }
            >
              {loading ? (
                <Stack spacing={1.25}>
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" height={40} />
                  ))}
                </Stack>
              ) : rankedFiltered.length === 0 ? (
                <EmptyHint
                  text={
                    progressFilter === 'cleared'
                      ? 'クリア済みの課題はありません'
                      : '未クリアの課題はありません'
                  }
                />
              ) : (
                <Stack spacing={1}>
                  {rankedFiltered.slice(0, 6).map((a) => {
                    const done = a.rate === 100 && a.totalQuestionCount > 0;
                    return (
                      <Box
                        key={a.assignmentId}
                        onClick={() =>
                          navigate(`/training-learn/learn-assignment/${a.assignmentId}`)
                        }
                        sx={{
                          cursor: 'pointer',
                          mx: -1,
                          px: 1,
                          py: 0.75,
                          borderRadius: 1.5,
                          transition: 'background-color 0.15s',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                          {done ? (
                            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : a.clearedQuestionCount > 0 ? (
                            <HourglassEmptyIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                          ) : (
                            <RadioButtonUncheckedIcon
                              sx={{ fontSize: 16, color: 'text.disabled' }}
                            />
                          )}
                          <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                            {a.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {a.rate}%
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={a.rate}
                          color={done ? 'success' : 'primary'}
                          sx={{ height: 5, borderRadius: 3 }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Widget>
          </Grid>

          {/* 未クリアの設問 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              title={`未クリアの設問${questionsLoading ? '' : `（${uncleared.length}）`}`}
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/training-learn/all-questions?filter=uncleared')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  すべて見る
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {questionsLoading ? (
                <Stack spacing={1}>
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" height={38} />
                  ))}
                </Stack>
              ) : uncleared.length === 0 ? (
                <EmptyHint text="未クリアの設問はありません。すべてクリア済みです。" />
              ) : (
                <Stack spacing={1}>
                  {uncleared.slice(0, 6).map((q) => (
                    <Stack
                      key={q.questionId}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      onClick={() =>
                        navigate(
                          `/training-learn/learn-assignment/${q.assignmentId}/question/${q.questionId}`,
                        )
                      }
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.75,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                      }}
                    >
                      <RadioButtonUncheckedIcon
                        sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                      />
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {q.title}
                      </Typography>
                      <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    </Stack>
                  ))}
                </Stack>
              )}
            </Widget>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

const EmptyHint: React.FC<{ text: string }> = ({ text }) => (
  <Box sx={{ py: 3, textAlign: 'center' }}>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Box>
);

export default TrainingLearnHome;
